import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { hiringApi } from "@/api/hiring";
import { approvalsApi } from "@/api/approvals";
import { issuesApi } from "@/api/issues";
import { goalsApi } from "@/api/goals";
import type { Issue } from "@/api/issues";
import type { Goal, GoalMessage } from "@/api/goals";
import { queryKeys } from "@/lib/query-keys";
import { MarkdownRenderer } from "@/components/chat/markdown-renderer";
import {
  Inbox as InboxIcon, UserPlus, ShieldCheck, AlertTriangle, Target,
  MessageSquare, HelpCircle, AlertOctagon, CheckCircle2,
  Send, ChevronDown, ChevronUp, Bot, Loader2,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { MobileInbox } from "@/pages/mobile/MobileInbox";

const messageTypeIcons: Record<string, typeof MessageSquare> = {
  question: HelpCircle,
  update: MessageSquare,
  blocker: AlertOctagon,
  completion: CheckCircle2,
};

const messageTypeLabels: Record<string, string> = {
  question: "Question",
  update: "Update",
  blocker: "Blocked",
  completion: "Completed",
};

const messageTypeVariant: Record<string, "warning" | "default" | "danger" | "success"> = {
  question: "warning",
  update: "default",
  blocker: "danger",
  completion: "success",
};

function AgentMessageCard({
  msg,
  agentName,
  goalTitle,
  tenantId,
}: {
  msg: GoalMessage;
  agentName: string;
  goalTitle: string;
  tenantId: string;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [replyText, setReplyText] = useState("");
  const isUnread = !msg.readAt;
  const Icon = messageTypeIcons[msg.messageType] ?? MessageSquare;
  const variant = messageTypeVariant[msg.messageType] ?? "default";
  const needsResponse = msg.messageType === "question" || msg.messageType === "blocker";

  const replyMutation = useMutation({
    mutationFn: () => goalsApi.sendMessage(msg.goalId, replyText),
    onSuccess: () => {
      setReplyText("");
      // Mark the original message as read
      goalsApi.markMessagesRead(msg.goalId);
      queryClient.invalidateQueries({ queryKey: ["goal-messages-inbox", tenantId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.messages(msg.goalId) });
      setExpanded(false);
    },
  });

  const markReadMutation = useMutation({
    mutationFn: () => goalsApi.markMessagesRead(msg.goalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goal-messages-inbox", tenantId] });
    },
  });

  return (
    <Card className={`border-l-4 transition-all ${isUnread ? "border-l-accent bg-accent/[0.02]" : "border-l-border"}`}>
      <CardContent className="py-0">
        {/* Header — clickable to expand */}
        <button
          className="flex items-start justify-between w-full py-4 text-left"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-start gap-3 min-w-0">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full mt-0.5 ${
              variant === "danger" ? "bg-danger/10" : variant === "warning" ? "bg-warning/10" : variant === "success" ? "bg-success/10" : "bg-accent/10"
            }`}>
              <Icon size={14} className={
                variant === "danger" ? "text-danger" : variant === "warning" ? "text-warning" : variant === "success" ? "text-success" : "text-accent"
              } />
            </div>
            <div className="min-w-0">
              <div className={`text-sm ${isUnread ? "font-semibold" : "font-medium"} text-text-primary ${expanded ? "" : "line-clamp-2"}`}>
                <MarkdownRenderer content={msg.content} />
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Bot size={11} className="text-text-muted" />
                <span className="text-xs text-text-muted">
                  {agentName}
                </span>
                <span className="text-xs text-text-muted">on</span>
                <span className="text-xs text-text-muted font-medium">{goalTitle}</span>
                <span className="text-xs text-text-muted">{new Date(msg.createdAt).toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-3 shrink-0">
            {isUnread && <div className="h-2 w-2 rounded-full bg-accent" />}
            <Badge variant={variant}>{messageTypeLabels[msg.messageType] ?? msg.messageType}</Badge>
            {expanded ? <ChevronUp size={14} className="text-text-muted" /> : <ChevronDown size={14} className="text-text-muted" />}
          </div>
        </button>

        {/* Expanded content */}
        {expanded && (
          <div className="pb-4 pt-0 border-t border-border mt-0">
            {/* Full message */}
            <div className="bg-surface-hover rounded-lg p-3 mt-3 mb-3">
              <div className="text-sm text-text-primary"><MarkdownRenderer content={msg.content} /></div>
            </div>

            {/* Reply section */}
            {needsResponse ? (
              <div className="space-y-2">
                <label className="text-xs font-medium text-text-secondary">Reply to {agentName}</label>
                <div className="flex gap-2">
                  <Input
                    placeholder={`Answer the ${msg.messageType === "blocker" ? "blocker" : "question"}...`}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && replyText.trim()) {
                        e.preventDefault();
                        replyMutation.mutate();
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    disabled={!replyText.trim() || replyMutation.isPending}
                    onClick={() => replyMutation.mutate()}
                  >
                    {replyMutation.isPending ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Send size={14} />
                    )}
                  </Button>
                </div>
                {replyMutation.isPending && (
                  <p className="text-xs text-text-muted flex items-center gap-1.5">
                    <Loader2 size={10} className="animate-spin" />
                    Sending to agent and waiting for response...
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {isUnread && (
                  <Button size="sm" variant="outline" onClick={() => markReadMutation.mutate()}>
                    <CheckCircle2 size={12} className="mr-1.5" />
                    Mark as Read
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setExpanded(false)}>
                  Dismiss
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function Inbox() {
  const isMobile = useIsMobile();
  if (isMobile) return <MobileInbox />;
  return <InboxDesktop />;
}

function InboxDesktop() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const { data: agents = [] } = useAgentsByCompany(tenantId);

  const { data: proposals = [] } = useQuery({
    queryKey: queryKeys.hiring.byCompany(tenantId),
    queryFn: () => hiringApi.list(tenantId),
  });

  const { data: approvals = [] } = useQuery({
    queryKey: queryKeys.approvals.byCompany(tenantId),
    queryFn: () => approvalsApi.list(tenantId),
  });

  const { data: allIssues = [] } = useQuery({
    queryKey: queryKeys.issues.byCompany(tenantId),
    queryFn: () => issuesApi.list(tenantId),
  });

  const { data: goals = [] } = useQuery({
    queryKey: queryKeys.goals.byCompany(tenantId),
    queryFn: () => goalsApi.list(tenantId),
  });

  const ownedGoalIds = goals
    .filter((g: Goal) => g.ownerId)
    .map((g: Goal) => g.id);

  const { data: allGoalMessages = [] } = useQuery({
    queryKey: ["goal-messages-inbox", tenantId],
    queryFn: async () => {
      const msgs: GoalMessage[] = [];
      for (const gid of ownedGoalIds) {
        const goalMsgs = await goalsApi.listMessages(gid);
        msgs.push(...goalMsgs);
      }
      return msgs;
    },
    enabled: ownedGoalIds.length > 0,
    refetchInterval: 30000,
  });

  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const recentAgentMessages = allGoalMessages
    .filter((m) => m.direction === "agent_to_human" && (m.createdAt > oneDayAgo || !m.readAt))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 20);

  const pendingProposals = proposals.filter((p) => p.status === "pending_approval" || p.status === "draft");
  const pendingApprovals = approvals.filter((a) => a.status === "pending");
  const blockedIssues = allIssues.filter((i: Issue) => i.status === "blocked");

  const issueGoalIds = new Set(allIssues.map((i: Issue) => i.goalId).filter(Boolean));
  const goalsNeedingPlanning = goals.filter(
    (g: Goal) => g.status === "active" && !issueGoalIds.has(g.id) && !g.ownerId,
  );

  const totalItems =
    recentAgentMessages.length +
    pendingProposals.length +
    pendingApprovals.length +
    blockedIssues.length +
    goalsNeedingPlanning.length;

  const getAgentName = (agentId: string | null) => {
    if (!agentId) return "Agent";
    return agents.find((a) => a.id === agentId)?.name || "Agent";
  };

  const getGoalTitle = (goalId: string) => {
    return goals.find((g: Goal) => g.id === goalId)?.title || "Goal";
  };

  const unreadCount = recentAgentMessages.filter((m) => !m.readAt).length;

  return (
    <CenterPanel title="Inbox">
      <div className="flex items-center gap-3 mb-4">
        <p className="text-sm text-text-secondary">
          {totalItems} item{totalItems !== 1 ? "s" : ""} needing attention
        </p>
        {unreadCount > 0 && (
          <Badge variant="warning">{unreadCount} unread</Badge>
        )}
      </div>

      {totalItems === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <InboxIcon size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">All clear. Nothing needs your attention.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Agent goal messages — expandable with inline reply */}
          {recentAgentMessages.map((msg) => (
            <AgentMessageCard
              key={`msg-${msg.id}`}
              msg={msg}
              agentName={getAgentName(msg.agentId)}
              goalTitle={getGoalTitle(msg.goalId)}
              tenantId={tenantId}
            />
          ))}

          {/* Pending approvals */}
          {pendingApprovals.map((approval) => (
            <Card key={`approval-${approval.id}`}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <ShieldCheck size={18} className="text-warning shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">{approval.title}</p>
                    <p className="text-xs text-text-muted">Pending approval</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Pending hire proposals */}
          {pendingProposals.map((proposal) => (
            <Card key={`hire-${proposal.id}`}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <UserPlus size={18} className="text-accent shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">Hire proposal: {proposal.proposedName}</p>
                    <p className="text-xs text-text-muted capitalize">{proposal.proposedRole} - {proposal.status.replace("_", " ")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Blocked issues */}
          {blockedIssues.map((issue: Issue) => (
            <Card key={`issue-${issue.id}`}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle size={18} className="text-danger shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">{issue.title}</p>
                    <p className="text-xs text-text-muted">Blocked issue{issue.identifier && ` (${issue.identifier})`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="danger">Blocked</Badge>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/tenants/${tenantId}/issues/${issue.id}`)}>
                    View
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Goals needing planning */}
          {goalsNeedingPlanning.map((goal: Goal) => (
            <Card key={`goal-${goal.id}`}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <Target size={18} className="text-warning shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">{goal.title}</p>
                    <p className="text-xs text-text-muted">Goal needs planning (no tasks)</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate(`/tenants/${tenantId}/goals/${goal.id}`)}>
                  Plan
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </CenterPanel>
  );
}
